import { AxiosError } from 'axios';
import DraggableButton from 'components/common/draggable-button/DraggableButton';
import useLanguage from 'hooks/feature/language/useLanguage';
import { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import DropDownBtn from '../select/DropDownBtn';
import { Language, LanguageExposureName, LanguageName, UpdateLanguageType } from 'types/feature/multi-lang/language';
import RadioContainer from 'components/common/radio/RadioContainer';
import ConfirmPopup from 'components/feature/workspace/popup/ConfirmPopup';
import AlertNoKeybordPopup from 'components/feature/workspace/popup/AlertNoKeybordPopup';

type LanguageRowsProps = {
  provided: DraggableProvided;
  language: Language;
  getLanguageList: () => Promise<void>;
};

const LanguageRows = ({ provided, language, getLanguageList }: LanguageRowsProps) => {
  const { updateLanguage, updateMainLanguage } = useLanguage();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [openDisplayConfirmPopup, setOpenDisplayConfirmPopup] = useState(false);
  const [openMainLangConfirmPopup, setOpenMainLangConfirmPopup] = useState(false);

  //  application 에 노출할지 여부 (노출 여부지만 지도기반으로 변경되면서 사실상 사용 여부임)
  const isDisabledLanguage = !language.display;
  const handleChangeRadioBtn = () => {
    setOpenMainLangConfirmPopup(true);
  };

  const handleAlert = (message: string) => {
    setOpenAlert(true);
    setAlertMessage(message);
  };

  // 사용 여부
  const updateLanguageDisplay = async () => {
    try {
      if (language.main) {
        setOpenDisplayConfirmPopup(false);
        handleAlert('메인 언어는 사용 여부를 변경할 수 없습니다.');
        return;
      }
      if (language.id) {
        await updateLanguage(language.id, { display: !language.display });
        await getLanguageList();
        setOpenDisplayConfirmPopup(false);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 400) {
          handleAlert(e.message);
        }
      }
    }
  };

  // 메인언어 변경
  const changeMainLanguage = async () => {
    try {
      if (language.id) {
        await updateMainLanguage(language.id);
        await getLanguageList();
        setOpenMainLangConfirmPopup(false);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status && e.status === 400) {
          handleAlert(e.message);
        }
      }
    }
  };
  return (
    <>
      <tr
        className={`h-12 ${isDisabledLanguage && 'bg-bg-f9 cursor-default'}`}
        {...provided.draggableProps}
        ref={provided.innerRef}
        key={language.code}
      >
        <td {...provided.dragHandleProps}>
          <div className='flex items-center justify-center'>{!isDisabledLanguage && <DraggableButton />}</div>
        </td>
        <td>
          <RadioContainer
            disabled={isDisabledLanguage}
            value={language.code}
            checked={language.main}
            onChange={handleChangeRadioBtn}
          />
        </td>
        <td className={`text-center ${isDisabledLanguage && 'text-gray-99 '}`}>{LanguageName[language.code]}</td>
        <td className={`text-center ${isDisabledLanguage && 'text-gray-99'}`}>
          {language.displayName || LanguageExposureName[language.code]}
        </td>
        <td>
          <DropDownBtn
            language={language}
            name={UpdateLanguageType.display}
            currentState={language.display}
            openConfirm={setOpenDisplayConfirmPopup}
            options={[{ name: '사용' }, { name: '미사용' }]}
          />
        </td>
      </tr>

      {openDisplayConfirmPopup && (
        <ConfirmPopup
          message='사용 여부를 변경 하시겠습니까?'
          okMessage='변경'
          cancelMessage='취소'
          onClickOk={updateLanguageDisplay}
          onClickCancel={() => setOpenDisplayConfirmPopup(false)}
        />
      )}

      {openMainLangConfirmPopup && (
        <ConfirmPopup
          message='해당 언어를 메인 언어로 변경 하시겠습니까?'
          okMessage='변경'
          cancelMessage='취소'
          onClickOk={changeMainLanguage}
          onClickCancel={() => setOpenMainLangConfirmPopup(false)}
        />
      )}

      {openAlert && <AlertNoKeybordPopup message={alertMessage} onClick={() => setOpenAlert(false)} />}
    </>
  );
};

export default LanguageRows;
